// @ts-nocheck

import { filter, reduce, map, mapValues, keyBy, sortBy, find, orderBy } from 'lodash';
import { createSelector } from 'reselect';

import { type BasketItemState } from '../states/basket';
import { type BasketItems } from '../states/basket';
import BetBuilder from '../states/betbuilder';
import { type EntitiesBets } from '../states/entities/bets';
import { type EntitiesMatches } from '../states/entities/matches';
import { type EntitiesOutcomes } from '../states/entities/outcomes';
import { type StateType } from '../states/index';
import { BasketItemStatuses } from '../types/basket';
import { type Selector } from '../types/selectors';
import BasketUtils from '../utils/basket';
import { createResultEqualSelector, createShallowEqualSelector } from '../utils/reselect';

import { getItemIdOrOddIdFromProps } from './_propsGetters_';
import { selectBasketIsFromFreebets, selectBasketItems, selectBasketStatus } from './basket/basketPrimary.selector';
import { selectEntitiesBets } from './entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesMatches } from './entities/matches/entitiesMatchesPrimary.selector';
import { selectEntitiesOutcomes } from './entities/outcomes/entitiesOutcomesPrimary.selector';

const selectBasketDefaultItems: Selector<BasketItems> = createResultEqualSelector([selectBasketItems], (items) =>
  Object.keys(items)
    .filter((key) => !items[key].betbuilder)
    .reduce((obj, key) => {
      obj[key] = items[key];
      return obj;
    }, {}),
);

const selectBasketBetbuilderItems: Selector<BasketItems> = createResultEqualSelector([selectBasketItems], (items) =>
  Object.keys(items)
    .filter((key) => !!items[key].betbuilder)
    .reduce((obj, key) => {
      obj[key] = items[key];
      return obj;
    }, {}),
);

const selectBasketBetbuilderItemsMatches: Selector<EntitiesMatches> = createSelector(
  [selectBasketBetbuilderItems, selectEntitiesMatches],
  (betbuilderItems, matches) =>
    reduce(
      betbuilderItems,
      (result, { selectionItems }) => {
        map(selectionItems, ({ matchId }) => {
          result[matchId] = matches[matchId];
        });
        return result;
      },
      {},
    ),
);

const selectBasketBetbuilderItemsBets: Selector<EntitiesBets> = createSelector(
  [selectBasketBetbuilderItems, selectEntitiesBets],
  (betbuilderItems, bets) =>
    reduce(
      betbuilderItems,
      (result, { selectionItems }) => {
        map(selectionItems, ({ betId }) => {
          result[betId] = bets[betId];
        });
        return result;
      },
      {},
    ),
);

const selectBasketBetbuilderItemsOutcomes: Selector<EntitiesOutcomes> = createSelector(
  [selectBasketBetbuilderItems, selectEntitiesOutcomes],
  (betbuilderItems, outcomes) =>
    reduce(
      betbuilderItems,
      (result, { selectionItems }) => {
        map(selectionItems, ({ outcomeId, outcomeIds }) => {
          if (outcomeIds) {
            outcomeIds.forEach((multiOutcomeId) => (result[multiOutcomeId] = outcomes[multiOutcomeId]));
          } else {
            result[outcomeId] = outcomes[outcomeId];
          }
        });
        return result;
      },
      {},
    ),
);

// Typing to fix with TS
const selectBasketFinalBetbuilderItems: Selector<unknown> = createShallowEqualSelector(
  [
    selectBasketBetbuilderItems,
    selectBasketBetbuilderItemsMatches,
    selectBasketBetbuilderItemsBets,
    selectBasketBetbuilderItemsOutcomes,
  ],
  (items, matches, bets, outcomes) =>
    map(items, (item) => {
      const match = matches[item.matchId];

      const selectionItems = item.selectionItems.reduce((result, item) => {
        let enhanceItem = { item, match, bet: bets[item.betId] };

        if (BetBuilder.isMultichoiceItem(item)) {
          enhanceItem = {
            ...enhanceItem,
            outcomes: item.outcomeIds.map((outcomeId) => outcomes[outcomeId]),
          };
          parseInt(2);
        } else {
          enhanceItem = {
            ...enhanceItem,
            outcome: outcomes[item.outcomeId],
          };
        }

        result.push(enhanceItem);

        return result;
      }, []);

      return {
        ...item,
        selectionItems,
      };
    }),
);

export const selectBasketItemsWithSelectedBonusesValues: Selector<Array<BasketItemState>> = createResultEqualSelector(
  selectBasketItems,
  (items) => {
    const itemsWithBonusId = filter(items, (item) => item.selectedBonusId);
    const itemsWithSelectedPromotionValues = itemsWithBonusId
      .map((item) => find(item.eligibleBonuses, (bonus) => bonus.promotionId === item.selectedBonusId))
      .filter((k) => k);

    return orderBy(itemsWithSelectedPromotionValues, 'promotionId', 'asc');
  },
);

const selectBasketPotentialGainPerOddId: Selector<{ [oddId: number]: number }> = createResultEqualSelector(
  [selectBasketItems, selectBasketIsFromFreebets],
  (items, isFromFreeBets) =>
    mapValues(keyBy(items, 'oddId'), ({ stake, value, validatedValue, selectedFreebetId }) =>
      BasketUtils.computePotentialGain(validatedValue || value, stake, !!selectedFreebetId || isFromFreeBets),
    ),
);

const selectBasketPossibleBonusesPerOddId: Selector<{ [oddId: number]: Array<number> }> = createResultEqualSelector(
  [selectBasketItems, selectBasketItemsWithSelectedBonusesValues],
  (items, eligibleBonuses) =>
    mapValues(keyBy(items, 'oddId'), ({ selectedBonusId }) => {
      let possibleBonus = 0;

      if (selectedBonusId && eligibleBonuses.filter((x) => x.promotionId === selectedBonusId).length) {
        possibleBonus = eligibleBonuses.filter((x) => x.promotionId === selectedBonusId)[0].possibleBonus;
      }

      return possibleBonus;
    }),
);

const selectBasketAggregatedItems: Selector<Array<BasketItemState>> = createResultEqualSelector(
  [selectBasketDefaultItems, selectBasketFinalBetbuilderItems],
  (defaultItems, betbuilderItems) => [...map(defaultItems, (item) => item), ...betbuilderItems],
);

type BasketEnhancedItemType = BasketItemState & {
  potentialGain?: number;
  possibleBonus?: number;
};

// Typing to fix with TS
export const selectBasketEnhancedItems: Selector<Array<BasketEnhancedItemType>> = createResultEqualSelector(
  [
    selectBasketAggregatedItems,
    selectBasketPotentialGainPerOddId,
    selectBasketPossibleBonusesPerOddId,
    selectBasketStatus,
  ],
  (basketItems, potentialGain, simpleModeTotalGain) =>
    sortBy(
      map(basketItems, (item) => ({
        ...item,
        potentialGain: potentialGain[item.oddId],
        possibleBonus: simpleModeTotalGain[item.oddId],
      })),
      'sortBy',
    ),
);

// no-memoize
export const selectBasketNbItemInError: Selector<number> = (state: StateType) =>
  reduce(
    selectBasketAggregatedItems(state),
    (acc, { status }) => (status === BasketItemStatuses.ERROR ? acc + 1 : acc),
    0,
  );

export const selectBasketEnhancedItem: Selector<BasketItemState | undefined> = createResultEqualSelector(
  [selectBasketEnhancedItems, getItemIdOrOddIdFromProps],
  (items, itemId) => find(items, (item) => item.oddId === itemId),
);

// no-memoize
export const getNbItems: Selector<number> = (state: StateType) => selectBasketEnhancedItems(state).length;

// no-memoize
export const selectNbBasketItems: Selector<number> = (state: StateType) =>
  Object.keys(selectBasketItems(state) ?? {}).length;

export const selectBasketItemsMatchIds: Selector<Array<number>> = createResultEqualSelector(
  [selectBasketItems],
  (items) => Object.keys(items).map((oddsId) => items[oddsId].matchId),
);
